import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { Col, Row } from "components/Containers";
import { colors, fontSize } from "style";

/**
 * InlineProgressBar
 * Compact progress display in the form of: [bar] [%], or with a label above it:
 * [label] [%] over [bar]
 *
 * @param {Number} completed
 * @param {Number} total
 * @param {Node}   label      Shown above the bar, with the percentage to its right
 */
const InlineProgressBar = ({ completed, total, label, ...props }) => {
  const safeTotal = total > 0 ? total : 0;
  const percentage = safeTotal > 0 ? Math.round((completed / safeTotal) * 100) : 0;
  const bar = (inline) => (
    <div css={[styles.track, inline ? styles.track_inline : styles.track_full]}>
      <div css={styles.fill(percentage)} />
    </div>
  );

  if (label) {
    return (
      <Col gap="0.4rem" {...props}>
        <Row css={styles.header}>
          {label}
          <span css={styles.value}>{percentage}%</span>
        </Row>
        {bar(false)}
      </Col>
    );
  }

  return (
    <Row css={styles.row} {...props}>
      {bar(true)}
      <span css={styles.value}>{percentage}%</span>
    </Row>
  );
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  header: css`
    justify-content: space-between;
    gap: 0.5rem;
  `,
  value: css`
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.xxsmall};
    font-weight: 600;
    flex-shrink: 0;
  `,
  track: css`
    height: 0.6rem;
    border-radius: 999px;
    background: ${colors.grayAnatomyLight4};
    overflow: hidden;
  `,
  track_inline: css`
    flex: 1;
  `,
  track_full: css`
    width: 100%;
  `,
  fill: (percentage) => css`
    width: ${percentage}%;
    height: 100%;
    border-radius: 999px;
    background: ${colors.purpleRainBase};
  `,
};

InlineProgressBar.propTypes = {
  completed: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  label: PropTypes.node,
};

export default InlineProgressBar;
